import { buildActivityEligibleVenuesPath, readActivityIdentifier } from '@liquid-spirit/api/domains/activities';
import { createVenuesClient, readVenueListResponse, readVenueDetailResponse, readVenuePreviewResponse, readVenueEligibilityResponse, readActivityVenueEligibilityResponse, readRelatedVenueResponse, toEligibleVenueOption, toRelatedVenueOption } from '@liquid-spirit/api/domains/venues';
import { createVenueRequestsClient } from '@liquid-spirit/api/domains/venue-requests';
import apiClient from './apiClient';
import { createCacheAdapter } from './apiCacheAdapter.js';
import { invalidateVenueCaches } from './frontendCacheInvalidation.js';

const isAbortError = (error) => {
  if (!error) return false;
  if (error.name === 'AbortError' || error.code === 'ABORT_ERR') return true;
  const message = String(error.message || '').toLowerCase();
  return message.includes('aborted');
};

const makeRequest = async (url, method, body = null, config = {}) => {
  try {
    return await apiClient.request(url, {
      method,
      body,
      params: config.params,
      headers: config.headers || {},
      signal: config.signal,
      token: config.token,
      cacheAdapter: config.cacheAdapter,
      contract: 'payload',
    });
  } catch (error) {
    if (!isAbortError(error) && !config.suppressErrors) {
      console.error(`Error in ${method} request to ${url}:`, error);
    }
    throw error;
  }
};

// /api/venues/* paths live in @liquid-spirit/api; caching and invalidation stay
// here. The member venue-request endpoints use the venue-requests domain client
// below; the activity eligible-venues endpoint keeps its literal path.
const venues = createVenuesClient({
  get: (path, { params, ...config } = {}) => makeRequest(path, 'GET', null, { ...config, params }),
  post: (path, body, config = {}) => makeRequest(path, 'POST', body, config),
  patch: (path, body, config = {}) => makeRequest(path, 'PATCH', body, config),
  put: (path, body, config = {}) => makeRequest(path, 'PUT', body, config),
});

const venueRequests = createVenueRequestsClient({
  get: (path, config = {}) => makeRequest(path, 'GET', null, config),
  post: (path, body, config = {}) => makeRequest(path, 'POST', body, config),
});

export const fetchVenues = (communityId, options = {}) =>
  venues.list({
    ...options,
    params: communityId ? { community: communityId } : {},
    cacheAdapter: createCacheAdapter({
      key: `venues:list:${communityId || 'all'}`,
      staleMs: options.staleMs,
      revalidate: options.revalidate,
      credentialScoped: true,
      validateResponse: readVenueListResponse,
    }),
  }).then(readVenueListResponse);

export const findVenuesForIds = (facilitatorIds = [], participantIds = [], options = {}) => {
  const {
    signal,
    userId,
    ...restOptions
  } = options || {};
  const payload = { facilitatorIds, participantIds };
  if (userId) {
    payload.userId = userId;
  }
  return venues.find(payload, { ...restOptions, signal })
    .then(readRelatedVenueResponse).then(rows => rows.map(toRelatedVenueOption));
};

export const createVenue = (venue) =>
  venues.create(venue).then(readVenueDetailResponse).then((result) => {
    invalidateVenueCaches({
      communityId: result.community,
      venueId: result._id,
    });
    return result;
  });

export const createCommunityVenue = (venue) =>
  venues.create({ ...venue, type: 'CommunityVenue' }).then(readVenueDetailResponse).then((result) => {
    invalidateVenueCaches({
      communityId: result.community,
      venueId: result._id,
    });
    return result;
  });

export const createVenueForUser = (payload) =>
  venues.createForUser(payload).then(readVenueDetailResponse).then((result) => {
    invalidateVenueCaches({
      communityId: result.community,
      venueId: result._id,
    });
    return result;
  });

export const createResidenceVenueFromUserAddress = (payload = null, options = {}) => {
  const body = payload && Object.keys(payload).length > 0 ? payload : {};
  return venues.createResidenceFromUserAddress(body, options).then(readVenueDetailResponse).then((result) => {
    invalidateVenueCaches({ communityId: result.community, venueId: result._id });
    return result;
  });
};

export const updateVenue = async (id, updates) => {
  const result = readVenueDetailResponse(await venues.update(id, updates), 'venue update');
  invalidateVenueCaches({ communityId: result.community, venueId: result._id });
  return result;
};

// Fetch venues available for session creation on an activity.
// Uses the newer eligible-venues endpoint (same logic family as mobile).
export const fetchVenuesForActivity = async (activityId, options = {}) => {
  readActivityIdentifier(activityId);
  const response = await makeRequest(
    buildActivityEligibleVenuesPath(activityId),
    'GET',
    null,
    options,
  );

  return readActivityVenueEligibilityResponse(response).map(toEligibleVenueOption);
};

// Back-compat alias used elsewhere in the UI
export const fetchAvailableVenuesForActivity = (activityId, options = {}) =>
  fetchVenuesForActivity(activityId, options);

// New venue request workflow
export const fetchVenueRequestInbox = (options = {}) =>
  venueRequests.fetchInbox(options);

export const approveVenueRequest = (requestId, payload = {}, options = {}) =>
  venueRequests.approve(requestId, payload, options);

export const declineVenueRequestById = (requestId, payload = {}, options = {}) =>
  venueRequests.decline(requestId, payload, options);

export const cancelVenueRequestById = (requestId, options = {}) =>
  venueRequests.cancel(requestId, options);

// VENUE: eligibility + preview
export const fetchEligibleVenues = ({ resourceType, resourceId }, options = {}) =>
  venues.eligible({ resourceType, resourceId }, {
    ...options,
    cacheAdapter: createCacheAdapter({
      key: `venues:eligible:${resourceType}:${resourceId}`,
      staleMs: options.staleMs,
      revalidate: options.revalidate,
      credentialScoped: true,
      validateResponse: readVenueEligibilityResponse,
    }),
  }).then(readVenueEligibilityResponse);

export const previewVenueUse = ({ resourceType, resourceId, venueId }, options = {}) =>
  venues.preview({ resourceType, resourceId, venueId }, options).then(readVenuePreviewResponse);
